import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaUpload, FaPaperPlane } from 'react-icons/fa';
import { formatEducation } from '../utils/education';
import { supabase } from '../lib/supabase';
import { notifyNewRequest } from '../lib/emailService';
import { useLanguage } from '../contexts/LanguageContext';

const RequestAlumni = () => {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [s1Kampus, setS1Kampus] = useState('');
  const [s1Jurusan, setS1Jurusan] = useState('');
  const [s2Kampus, setS2Kampus] = useState('');
  const [s2Jurusan, setS2Jurusan] = useState('');
  const [s3Kampus, setS3Kampus] = useState('');
  const [s3Jurusan, setS3Jurusan] = useState('');
  const [job, setJob] = useState('');
  const [institution, setInstitution] = useState('');
  const [researchInterests, setResearchInterests] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [googleScholar, setGoogleScholar] = useState('');
  const [tahunPimnas, setTahunPimnas] = useState('');
  const [domisili, setDomisili] = useState('');
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 1 * 1024 * 1024) {
      setErrorMessage(t('request.photoSizeError') || 'Max photo size 1 MB.');
      e.target.value = '';
      return;
    }
    setErrorMessage('');
    setPhoto(file);
    if (file) setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    if (!name.trim()) {
      setErrorMessage(t('request.nameRequired') || 'Full name is required.');
      setLoading(false);
      return;
    }

    try {
      let photoUrl = null;

      if (photo) {
        const fileExt = photo.name.split('.').pop();
        const fileName = `request-${(email || name).replace(/[^a-zA-Z0-9]/g, '')}-${Date.now()}.${fileExt}`;
        const filePath = `alumni-photos/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('alumni-photos')
          .upload(filePath, photo, { contentType: photo.type, cacheControl: '3600', upsert: false });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage.from('alumni-photos').getPublicUrl(filePath);
        photoUrl = urlData.publicUrl;
      }

      const { error } = await supabase.from('alumni_requests').insert([{
        name: name.trim(),
        email: email.trim() || null,
        education: formatEducation({ s1_kampus: s1Kampus, s1_jurusan: s1Jurusan, s2_kampus: s2Kampus, s2_jurusan: s2Jurusan, s3_kampus: s3Kampus, s3_jurusan: s3Jurusan }),
        job: job.trim() || null,
        institution: institution.trim() || null,
        research_interests: researchInterests.trim() || null,
        tahun_pimnas: tahunPimnas ? parseInt(tahunPimnas, 10) : null,
        domisili: domisili.trim() || null,
        linkedin: linkedin || null,
        google_scholar: googleScholar || null,
        photo_url: photoUrl,
        message: message || null,
        status: 'pending',
      }]);

      if (error) throw error;

      // Send email notification to admin (non-blocking)
      notifyNewRequest({
        name: name.trim(),
        email: email.trim() || null,
        job: job.trim() || null,
        institution: institution.trim() || null,
        tahun_pimnas: tahunPimnas ? parseInt(tahunPimnas, 10) : null,
        domisili: domisili.trim() || null,
        research_interests: researchInterests.trim() || null,
        message: message || null,
      });

      setSuccess(true);
    } catch (error) {
      console.error('Error submitting request:', error);
      setErrorMessage((t('request.submitError') || 'Failed to submit') + ': ' + (error.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';
  const optLabel = <span className="text-gray-400 font-normal">({t('request.optional')})</span>;

  if (success) {
    return (
      <div className="font-inter bg-gray-50 min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">✅</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('request.successTitle')}</h2>
          <p className="text-gray-500 mb-6">
            {t('request.successDesc')}, <span className="font-semibold text-gray-700">{name}</span>. {t('request.successDesc2')}
          </p>
          <Link
            to="/alumni"
            className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-xl transition"
          >
            {t('request.viewDirectory')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="font-inter bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/alumni" className="text-gray-300 hover:text-white text-sm inline-flex items-center gap-1 mb-4 transition-colors">
            <FaArrowLeft /> {t('request.backToDirectory')}
          </Link>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FaPaperPlane className="text-gold-400" /> {t('request.title')}
          </h1>
          <p className="text-gray-300 mt-2">{t('request.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-emerald-500 to-primary-500"></div>

          <div className="p-6 md:p-8">
            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-xl mb-6 text-sm flex items-start gap-2">
              <span className="text-lg">ℹ️</span>
              <span>{t('request.infoBox')}</span>
            </div>

            {errorMessage && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm">
                {errorMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Photo Upload */}
              <div className="flex flex-col items-center mb-2">
                <div className="relative mb-3">
                  {preview ? (
                    <img src={preview} alt="Preview" className="w-24 h-24 rounded-full object-cover ring-4 ring-primary-100" />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                      <FaUpload className="text-2xl" />
                    </div>
                  )}
                </div>
                <label className="cursor-pointer text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors">
                  {t('request.uploadPhoto')} {optLabel}
                  <input type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
                </label>
              </div>

              {/* Identitas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>{t('request.fullName')} *</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} required placeholder={t('request.fullName')} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>{t('login.email')} {optLabel}</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="alumni@email.com" className={inputClass} />
                </div>
              </div>

              {/* Tahun PIMNAS */}
              <div className="bg-gold-500/5 border border-gold-500/20 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  🏆 {t('request.pimnasParticipation')}
                </h3>
                <div className="max-w-xs">
                  <label className={labelClass}>{t('request.pimnasEdition')} {optLabel}</label>
                  <input type="number" min="1" max="50" value={tahunPimnas} onChange={e => setTahunPimnas(e.target.value)} placeholder="Contoh: 33" className={inputClass} />
                  <p className="text-xs text-gray-400 mt-1">{t('request.pimnasHint')}</p>
                </div>
              </div>

              {/* Pendidikan Section */}
              <div className="bg-gray-50 rounded-xl p-5 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  🎓 {t('request.educationHistory')} <span className="text-xs text-gray-400 font-normal">— {t('request.fillRelevant')}</span>
                </h3>

                {/* S1 */}
                <div>
                  <p className="text-xs font-semibold text-primary-600 mb-2">{t('request.s1Almamater')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>{t('request.campus')} {optLabel}</label>
                      <input type="text" value={s1Kampus} onChange={e => setS1Kampus(e.target.value)} placeholder="Universitas / Institut" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>{t('request.major')} {optLabel}</label>
                      <input type="text" value={s1Jurusan} onChange={e => setS1Jurusan(e.target.value)} placeholder="Teknik Informatika, Biologi, dll" className={inputClass} />
                    </div>
                  </div>
                </div>

                {/* S2 */}
                <div>
                  <p className="text-xs font-semibold text-blue-600 mb-2">{t('request.s2Master')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>{t('request.campus')} {optLabel}</label>
                      <input type="text" value={s2Kampus} onChange={e => setS2Kampus(e.target.value)} placeholder="Universitas S2" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>{t('request.major')} {optLabel}</label>
                      <input type="text" value={s2Jurusan} onChange={e => setS2Jurusan(e.target.value)} placeholder="Jurusan S2" className={inputClass} />
                    </div>
                  </div>
                </div>

                {/* S3 */}
                <div>
                  <p className="text-xs font-semibold text-purple-600 mb-2">{t('request.s3Doctor')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>{t('request.campus')} {optLabel}</label>
                      <input type="text" value={s3Kampus} onChange={e => setS3Kampus(e.target.value)} placeholder="Universitas S3" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>{t('request.major')} {optLabel}</label>
                      <input type="text" value={s3Jurusan} onChange={e => setS3Jurusan(e.target.value)} placeholder="Jurusan S3" className={inputClass} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Karir */}
              <div className="bg-gray-50 rounded-xl p-5 space-y-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  💼 {t('request.careerSection')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>{t('request.jobTitle')} {optLabel}</label>
                    <input type="text" value={job} onChange={e => setJob(e.target.value)} placeholder="Dosen, Peneliti, Software Engineer, dll" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>{t('request.workplace')} {optLabel}</label>
                    <input type="text" value={institution} onChange={e => setInstitution(e.target.value)} placeholder="Universitas, Perusahaan, Lembaga, dll" className={inputClass} />
                  </div>
                </div>
              </div>

              {/* Minat Riset */}
              <div>
                <label className={labelClass}>{t('request.researchInterests')} {optLabel}</label>
                <input type="text" value={researchInterests} onChange={e => setResearchInterests(e.target.value)} placeholder="Contoh: Affective Computing, NLP, IoT, Bioteknologi" className={inputClass} />
                <p className="text-xs text-gray-400 mt-1">{t('request.researchHint')}</p>
              </div>

              {/* Domisili */}
              <div>
                <label className={labelClass}>{t('request.currentDomicile')} {optLabel}</label>
                <input type="text" value={domisili} onChange={e => setDomisili(e.target.value)} placeholder="Contoh: Jakarta, Bandung, Surabaya" className={inputClass} />
                <p className="text-xs text-gray-400 mt-1">{t('request.domicileHint')}</p>
              </div>

              {/* Profil Online */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>LinkedIn {optLabel}</label>
                  <input type="url" value={linkedin} onChange={e => setLinkedin(e.target.value)} placeholder="https://linkedin.com/in/..." className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Google Scholar {optLabel}</label>
                  <input type="url" value={googleScholar} onChange={e => setGoogleScholar(e.target.value)} placeholder="https://scholar.google.com/..." className={inputClass} />
                </div>
              </div>

              {/* Pesan opsional */}
              <div>
                <label className={labelClass}>{t('request.messageToAdmin')} {optLabel}</label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Ceritakan sedikit tentang pengalaman PIMNAS kamu..."
                  rows={3}
                  className={inputClass}
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-semibold py-3 px-6 rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-60"
                >
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> {t('request.submitting')}</>
                  ) : (
                    <><FaPaperPlane /> {t('request.submit')}</>
                  )}
                </button>
                <Link
                  to="/alumni"
                  className="inline-flex items-center justify-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium py-3 px-6 rounded-xl transition-colors text-center"
                >
                  {t('common.cancel')}
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestAlumni;
